/**
 * The one-off sound-effect catalogue — piece lock, line clear, hard drop and
 * the rest — built from the same synth primitives (`voices.ts`) the music
 * uses, so nothing in the game plays a sampled or licensed sound.
 */
import { noteFreq } from './notes.js';
import { playKick, playNoiseBurst, playSweep, playTone } from './voices.js';

export type SfxName =
  | 'lock'
  | 'hardDrop'
  | 'hold'
  | 'clear'
  | 'quad'
  | 'garbageWarning'
  | 'countdownTick'
  | 'countdownGo'
  | 'curseFired'
  | 'curseHit'
  | 'win'
  | 'lose';

export interface SfxOptions {
  /** `'clear'` only: how many lines, 1–3. A quad uses the distinct `'quad'` sound instead of `lines: 4`. */
  readonly lines?: number;
}

/**
 * Play one cue from the catalogue, starting at `startSec` on the given
 * context/destination. Every function here is a handful of short oscillator
 * or noise voices with their own envelopes — nothing sustains, nothing
 * loops, so two SFX firing close together (a clear immediately followed by
 * the next piece's lock) never leaves a stuck note behind.
 */
export function triggerSfx(
  ctx: BaseAudioContext,
  destination: AudioNode,
  name: SfxName,
  startSec: number,
  opts: SfxOptions = {},
): void {
  switch (name) {
    case 'lock':
      playTone(ctx, destination, {
        freq: noteFreq('A', 3),
        type: 'triangle',
        startSec,
        durationSec: 0.07,
        peakGain: 0.3,
      });
      return;

    case 'hardDrop':
      playKick(ctx, destination, startSec, 0.85);
      playNoiseBurst(ctx, destination, {
        startSec,
        durationSec: 0.06,
        peakGain: 0.3,
        filter: 'lowpass',
        filterHz: 800,
      });
      return;

    case 'hold':
      playTone(ctx, destination, {
        freq: noteFreq('E', 4),
        type: 'square',
        startSec,
        durationSec: 0.06,
        peakGain: 0.22,
      });
      return;

    case 'clear': {
      // Pitch rises with the line count — a single is a small blip, a triple
      // is a bright, almost-there run, so a quad's own distinct sound (below)
      // reads as the payoff rather than "one more of the same."
      const lines = Math.max(1, Math.min(3, Math.round(opts.lines ?? 1)));
      const steps: readonly [string, number][] = [
        ['C', 5],
        ['E', 5],
        ['G', 5],
      ];
      for (let i = 0; i < lines; i++) {
        const [note, octave] = steps[i] ?? steps[steps.length - 1]!;
        playTone(ctx, destination, {
          freq: noteFreq(note, octave),
          type: 'square',
          startSec: startSec + i * 0.05,
          durationSec: 0.16,
          peakGain: 0.32,
        });
      }
      return;
    }

    case 'quad': {
      // The triumphant hit: a fast ascending arpeggio into a bright sustained
      // chord — distinct from `clear`'s rising blips rather than a fourth
      // step of the same pattern.
      const arp: readonly [string, number][] = [
        ['C', 5],
        ['E', 5],
        ['G', 5],
        ['C', 6],
      ];
      arp.forEach(([note, octave], i) => {
        playTone(ctx, destination, {
          freq: noteFreq(note, octave),
          type: 'square',
          startSec: startSec + i * 0.045,
          durationSec: 0.12,
          peakGain: 0.34,
        });
      });
      for (const [note, octave] of [
        ['C', 5],
        ['E', 5],
        ['G', 5],
      ] as const) {
        playTone(ctx, destination, {
          freq: noteFreq(note, octave),
          type: 'triangle',
          startSec: startSec + 0.2,
          durationSec: 0.42,
          peakGain: 0.3,
        });
      }
      return;
    }

    case 'garbageWarning':
      playSweep(ctx, destination, {
        fromFreq: noteFreq('A', 3),
        toFreq: noteFreq('E', 3),
        type: 'sawtooth',
        startSec,
        durationSec: 0.14,
        peakGain: 0.22,
      });
      playSweep(ctx, destination, {
        fromFreq: noteFreq('A', 3),
        toFreq: noteFreq('E', 3),
        type: 'sawtooth',
        startSec: startSec + 0.18,
        durationSec: 0.14,
        peakGain: 0.22,
      });
      return;

    case 'countdownTick':
      playTone(ctx, destination, {
        freq: noteFreq('A', 4),
        type: 'square',
        startSec,
        durationSec: 0.05,
        peakGain: 0.24,
      });
      return;

    case 'countdownGo':
      playTone(ctx, destination, {
        freq: noteFreq('A', 5),
        type: 'square',
        startSec,
        durationSec: 0.16,
        peakGain: 0.3,
      });
      return;

    case 'curseFired':
      playSweep(ctx, destination, {
        fromFreq: noteFreq('E', 5),
        toFreq: noteFreq('E', 3),
        type: 'sawtooth',
        startSec,
        durationSec: 0.24,
        peakGain: 0.26,
      });
      return;

    case 'curseHit':
      playNoiseBurst(ctx, destination, {
        startSec,
        durationSec: 0.16,
        peakGain: 0.28,
        filter: 'bandpass',
        filterHz: 900,
      });
      playTone(ctx, destination, {
        freq: noteFreq('D', 3),
        type: 'triangle',
        startSec,
        durationSec: 0.2,
        peakGain: 0.22,
      });
      return;

    case 'win':
      (
        [
          ['C', 5, 0],
          ['E', 5, 0.09],
          ['G', 5, 0.18],
          ['C', 6, 0.28],
        ] as const
      ).forEach(([note, octave, offset]) => {
        playTone(ctx, destination, {
          freq: noteFreq(note, octave),
          type: 'square',
          startSec: startSec + offset,
          durationSec: 0.26,
          peakGain: 0.32,
        });
      });
      return;

    case 'lose':
      (
        [
          ['A', 4, 0],
          ['F', 4, 0.11],
          ['D', 4, 0.22],
          ['A', 3, 0.34],
        ] as const
      ).forEach(([note, octave, offset]) => {
        playTone(ctx, destination, {
          freq: noteFreq(note, octave),
          type: 'triangle',
          startSec: startSec + offset,
          durationSec: 0.3,
          peakGain: 0.28,
        });
      });
      return;
  }
}
